import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  alpha,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Inventory as InventoryIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as ShoppingCartIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Receipt as ReceiptIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

function ProductGrid({ 
  products, 
  loading, 
  onSelectProduct, 
  showCustomProduct = true,
  isFullscreen = false,
  fullscreenContainer = null,
}) {
  const { t } = useTranslation();
  const [quantities, setQuantities] = useState({});
  const [editedPrices, setEditedPrices] = useState({});
  const [priceEditMode, setPriceEditMode] = useState({});
  const [customProductDialogOpen, setCustomProductDialogOpen] = useState(false);
  const [customProduct, setCustomProduct] = useState({
    name: '',
    price: '',
    quantity: 1,
  });

  const getQuantity = (productId) => {
    return quantities[productId] || 0;
  };

  const setQuantity = (productId, value) => {
    const newValue = Math.max(0, parseInt(value) || 0);
    setQuantities(prev => ({ ...prev, [productId]: newValue }));
  };

  const incrementQuantity = (productId) => {
    const currentQty = quantities[productId] || 0;
    setQuantities(prev => ({ ...prev, [productId]: currentQty + 1 }));
  };

  const decrementQuantity = (productId) => {
    const currentQty = quantities[productId] || 0;
    
    if (currentQty > 0) {
      setQuantities(prev => ({ 
        ...prev, 
        [productId]: currentQty - 1
      }));
    }
  };

  // ✅ Get current price (edited or original)
  const getCurrentPrice = (product) => {
    if (editedPrices[product.Id] !== undefined) {
      return editedPrices[product.Id];
    }
    return product.SalePrice || product.Price || 0;
  };

  // ✅ Handle price edit
  const handlePriceEdit = (productId, value) => {
    const price = parseFloat(value) || 0;
    setEditedPrices(prev => ({ ...prev, [productId]: price }));
  };

  // ✅ Toggle price edit mode
  const togglePriceEditMode = (productId) => {
    setPriceEditMode(prev => ({ ...prev, [productId]: !prev[productId] }));
  };

  // ✅ Handle add to cart with edited price
  const handleAddToCart = (product) => {
    const quantity = getQuantity(product.Id);
    if (quantity > 0) {
      const currentPrice = getCurrentPrice(product);
      
      // Create a copy of the product with updated price
      const productWithPrice = {
        ...product,
        Price: currentPrice,
        SalePrice: currentPrice,
        OriginalPrice: product.SalePrice || product.Price || 0, // Store original price
        IsPriceEdited: editedPrices[product.Id] !== undefined, // Flag to indicate edited price
      };
      
      onSelectProduct(productWithPrice, quantity);
      
      // Reset quantity and exit edit mode after adding
      setQuantities(prev => ({ ...prev, [product.Id]: 0 }));
      setPriceEditMode(prev => ({ ...prev, [product.Id]: false }));
      
      if (editedPrices[product.Id] !== undefined) {
        toast.success(`${product.Name} added with custom price €${currentPrice.toFixed(2)}`);
      }
    }
  };

  const handleCustomProductAdd = () => {
    if (!customProduct.name.trim() || !customProduct.price || customProduct.quantity <= 0) {
      toast.error('Please fill all required fields');
      return;
    }

    const price = parseFloat(customProduct.price);
    const quantity = parseInt(customProduct.quantity);

    // ✅ REMOVED: VAT calculation - cart will handle it
    // Just use the price as entered by the user
    const tempProduct = {
      Id: `custom-${Date.now()}`,
      Name: customProduct.name.trim(),
      Price: price, // Price as entered (will be treated as base price in cart)
      SalePrice: price,
      Stock: 999,
      IsCustom: true,
      Quantity: quantity,
      VAT: 21, // Default VAT rate (cart will calculate VAT from this)
      _rowKey: Date.now() + Math.random(),
    };

    console.log('✅ Custom product added:', {
      name: tempProduct.Name,
      price: price,
      quantity: quantity,
      vatRate: 21,
    });

    onSelectProduct(tempProduct, tempProduct.Quantity);

    setCustomProduct({ name: '', price: '', quantity: 1 });
    setCustomProductDialogOpen(false);
    
    toast.success(`${tempProduct.Name} added to cart`);
  };

  const getProductImage = (product) => {
    if (product.ImageUrl) return product.ImageUrl;
    if (product.Images && product.Images.length > 0) return product.Images[0];
    return null;
  };

  const getStockColor = (stock) => {
    if (stock <= 0) return 'error';
    if (stock < 10) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  const hasNoProducts = !products || products.length === 0;

  return (
    <Box>
      {/* Product Grid */}
      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 1,
          pb: 2,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: alpha('#ccc', 0.1),
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha('#3498db', 0.3),
            borderRadius: '10px',
            '&:hover': {
              background: alpha('#3498db', 0.5),
            },
          },
        }}
      >
        {hasNoProducts ? (
          <Box>
            {/* Custom Product Card */}
            {showCustomProduct && (
              <Grid container spacing={2}>
                <Grid item xs={6} sm={6} md={4} lg={3} xl={3}>
                  <Card
                    onClick={() => setCustomProductDialogOpen(true)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s',
                      borderRadius: 2,
                      overflow: 'visible',
                      boxShadow: 2,
                      cursor: 'pointer',
                      border: '2px dashed',
                      borderColor: '#667eea',
                      bgcolor: alpha('#667eea', 0.02),
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                        bgcolor: alpha('#667eea', 0.05),
                        borderColor: '#5568d3',
                      },
                    }}
                  >
                    {/* Custom Product Icon */}
                    <Box
                      sx={{
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha('#667eea', 0.08),
                      }}
                    >
                      <AddCircleOutlineIcon sx={{ fontSize: 50, color: '#667eea' }} />
                    </Box>

                    <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          color: '#667eea',
                          textAlign: 'center',
                        }}
                      >
                        {t('add_custom_product') || 'Add Custom Product'}
                      </Typography>
                      
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.7rem',
                          color: 'text.secondary',
                          textAlign: 'center',
                        }}
                      >
                        {t('custom_product_description') || 'One-time product for this invoice'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* No Products Message */}
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              <InventoryIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('no_products_found') || 'No products found'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('add_custom_product_hint') || 'You can still add custom products using the card above'}
              </Typography>
            </Box>
          </Box>
        ) : (
          // ✅ Show products grid when products exist
          <Grid container spacing={2}>
            {/* Custom Product Card - Always First */}
            {showCustomProduct && (
              <Grid 
                item 
                xs={6}
                sm={6}
                md={4}
                lg={3}
                xl={3}
              >
                <Card
                  onClick={() => setCustomProductDialogOpen(true)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s',
                    borderRadius: 2,
                    overflow: 'visible',
                    boxShadow: 2,
                    cursor: 'pointer',
                    border: '2px dashed',
                    borderColor: '#667eea',
                    bgcolor: alpha('#667eea', 0.02),
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6,
                      bgcolor: alpha('#667eea', 0.05),
                      borderColor: '#5568d3',
                    },
                  }}
                >
                  {/* Custom Product Icon */}
                  <Box
                    sx={{
                      height: 80,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha('#667eea', 0.08),
                    }}
                  >
                    <AddCircleOutlineIcon sx={{ fontSize: 50, color: '#667eea' }} />
                  </Box>

                  <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        color: '#667eea',
                        textAlign: 'center',
                      }}
                    >
                      {t('add_custom_product') || 'Add Custom Product'}
                    </Typography>
                    
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.7rem',
                        color: 'text.secondary',
                        textAlign: 'center',
                      }}
                    >
                      {t('custom_product_description') || 'One-time product for this invoice'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Regular Products */}
            {products.map((product) => {
              const productImage = getProductImage(product);
              const currentQuantity = getQuantity(product.Id);
              const currentPrice = getCurrentPrice(product);
              const isPriceEditing = priceEditMode[product.Id];
              const isPriceEdited = editedPrices[product.Id] !== undefined;
              const maxStock = product.Stock || product.StockQuantity || 0;
              const canDecrement = currentQuantity > 0;
              const canAddToCart = currentQuantity > 0;
              
              return (
                <Grid item xs={6} sm={6} md={4} lg={3} xl={3} key={product.Id}>
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.2s',
                      borderRadius: 2,
                      overflow: 'visible',
                      boxShadow: 2,
                      border: isPriceEdited ? '2px solid #ff9800' : 'none',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    {/* Product Image or Icon */}
                    {productImage ? (
                      <CardMedia
                        component="img"
                        height="80"
                        image={productImage}
                        alt={product.Name}
                        sx={{
                          objectFit: 'cover',
                          bgcolor: '#f5f5f5',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 80,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha('#667eea', 0.08),
                        }}
                      >
                        <ShoppingCartIcon sx={{ fontSize: 40, color: alpha('#667eea', 0.4) }} />
                      </Box>
                    )}

                    <CardContent sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {/* Product Name */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.4,
                          minHeight: 18,
                        }}
                        title={product.Name}
                      >
                        {product.Name}
                      </Typography>

                      {/* ✅ FIXED: Responsive Price and Stock Section */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {/* Stock Chip - Always on top */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Chip
                            label={`Stock: ${maxStock}`}
                            size="small"
                            color={getStockColor(maxStock)}
                            sx={{ 
                              fontWeight: 600, 
                              fontSize: '0.65rem', 
                              height: 20, 
                              minWidth: 60,
                            }}
                          />
                        </Box>

                        {/* Price Section - Below stock */}
                        {isPriceEditing ? (
                          <TextField
                            type="number"
                            value={currentPrice}
                            onChange={(e) => handlePriceEdit(product.Id, e.target.value)}
                            onBlur={() => togglePriceEditMode(product.Id)}
                            autoFocus
                            fullWidth
                            size="small"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">€</InputAdornment>,
                              style: { 
                                fontSize: '1rem',
                                fontWeight: 700,
                                color: '#ff9800',
                              }
                            }}
                            inputProps={{ 
                              min: 0, 
                              step: 0.01,
                              style: { padding: '6px 8px' }
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  borderColor: '#ff9800',
                                  borderWidth: 2,
                                },
                              },
                            }}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                color: isPriceEdited ? '#ff9800' : 'primary.main',
                                fontWeight: 700,
                                fontSize: { xs: '0.95rem', sm: '1rem' },
                              }}
                            >
                              €{currentPrice.toFixed(2)}
                            </Typography>
                            <Tooltip title={t('edit_price') || 'Edit Price'}>
                              <IconButton
                                size="small"
                                onClick={() => togglePriceEditMode(product.Id)}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  color: isPriceEdited ? '#ff9800' : '#999',
                                  '&:hover': {
                                    color: '#ff9800',
                                    bgcolor: alpha('#ff9800', 0.1),
                                  },
                                }}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}

                        {/* ✅ Show original price if edited */}
                        {isPriceEdited && !isPriceEditing && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              textDecoration: 'line-through',
                              fontSize: '0.7rem',
                            }}
                          >
                            Original: €{(product.SalePrice || product.Price || 0).toFixed(2)}
                          </Typography>
                        )}
                      </Box>

                      {/* Quantity Controls */}
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.75, 
                          bgcolor: alpha('#667eea', 0.05),
                          borderRadius: 1.5,
                          p: 0.5,
                          mt: 0.5,
                        }}
                      >
                        {/* Decrement Button */}
                        <IconButton
                          size="small"
                          onClick={() => decrementQuantity(product.Id)}
                          disabled={!canDecrement}
                          sx={{
                            bgcolor: canDecrement ? '#ff5252' : '#ffcdd2',
                            color: 'white',
                            width: { xs: 26, sm: 28 },
                            height: { xs: 26, sm: 28 },
                            minWidth: { xs: 26, sm: 28 },
                            '&:hover': { 
                              bgcolor: canDecrement ? '#ff1744' : '#ffcdd2',
                            },
                            '&.Mui-disabled': { 
                              bgcolor: '#ffcdd2',
                              color: '#fff',
                              opacity: 0.6,
                            },
                            p: 0,
                          }}
                        >
                          <RemoveIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                        </IconButton>

                        {/* Quantity Input */}
                        <TextField
                          type="number"
                          value={currentQuantity}
                          onChange={(e) => setQuantity(product.Id, e.target.value)}
                          inputProps={{ 
                            min: 0,
                            style: { 
                              textAlign: 'center',
                              padding: '4px 2px',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                            } 
                          }}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            '& .MuiOutlinedInput-root': {
                              height: { xs: 26, sm: 28 },
                              '& fieldset': { border: 'none' },
                            },
                            '& input': {
                              color: currentQuantity > 0 ? '#667eea' : '#999',
                            }
                          }}
                        />

                        {/* Increment Button */}
                        <IconButton
                          size="small"
                          onClick={() => incrementQuantity(product.Id)}
                          sx={{
                            bgcolor: '#4caf50',
                            color: 'white',
                            width: { xs: 26, sm: 28 },
                            height: { xs: 26, sm: 28 },
                            minWidth: { xs: 26, sm: 28 },
                            '&:hover': { 
                              bgcolor: '#43a047',
                            },
                            p: 0,
                          }}
                        >
                          <AddIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                        </IconButton>
                      </Box>

                      {/* Add to Cart Button */}
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        startIcon={<ShoppingCartIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                        onClick={() => handleAddToCart(product)}
                        disabled={!canAddToCart}
                        sx={{
                          py: { xs: 0.6, sm: 0.75 },
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          fontWeight: 600,
                          borderRadius: 1.5,
                          textTransform: 'none',
                          minHeight: { xs: 30, sm: 32 },
                          background: canAddToCart
                            ? isPriceEdited 
                              ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
                              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                            : '#e0e0e0',
                          color: canAddToCart ? 'white' : '#9e9e9e',
                          boxShadow: canAddToCart ? '0 2px 6px rgba(102, 126, 234, 0.3)' : 'none',
                          '&:hover': {
                            background: canAddToCart
                              ? isPriceEdited
                                ? 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)'
                                : 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' 
                              : '#e0e0e0',
                            boxShadow: canAddToCart ? '0 3px 10px rgba(102, 126, 234, 0.4)' : 'none',
                            transform: canAddToCart ? 'translateY(-1px)' : 'none',
                          },
                          '&.Mui-disabled': {
                            background: '#e0e0e0',
                            color: '#9e9e9e',
                          },
                        }}
                      >
                        {t('add_to_cart') || 'Add to Cart'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* Custom Product Dialog */}
      {showCustomProduct && (
        <Dialog 
          open={customProductDialogOpen} 
          onClose={() => setCustomProductDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          container={isFullscreen ? fullscreenContainer : undefined}
          style={{ position: isFullscreen ? 'absolute' : undefined }}
        >
          <DialogTitle 
            sx={{ 
              fontWeight: 700, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
            }}
          >
            {t('add_custom_product') || 'Add Custom Product'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              {/* Product Name */}
              <TextField
                fullWidth
                label={t('product_name') || 'Product Name'}
                value={customProduct.name}
                onChange={(e) => setCustomProduct({ ...customProduct, name: e.target.value })}
                placeholder={t('enter_product_name') || 'Enter product name'}
                autoFocus
              />
              
              {/* Price */}
              <TextField
                fullWidth
                label={t('price') || 'Price'}
                type="number"
                value={customProduct.price}
                onChange={(e) => setCustomProduct({ ...customProduct, price: e.target.value })}
                placeholder="0.00"
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
              
              {/* Quantity */}
              <TextField
                fullWidth
                label={t('quantity') || 'Quantity'}
                type="number"
                value={customProduct.quantity}
                onChange={(e) => setCustomProduct({ ...customProduct, quantity: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1 }}
              />

              {/* ✅ REMOVED: Subtotal section */}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={() => setCustomProductDialogOpen(false)}
              sx={{ textTransform: 'none' }}
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button 
              onClick={handleCustomProductAdd}
              variant="contained"
              disabled={!customProduct.name.trim() || !customProduct.price || customProduct.quantity <= 0}
              sx={{
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                },
              }}
            >
              {t('add_to_cart') || 'Add to Cart'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

export default ProductGrid;